import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { TIMESERIES_DAYS } from '@/features/dashboard/constants';
import { useCurrentUser } from '@/hooks/useCurrentUser.hook';
import { ROUTES } from '@/routes/route.config';
import { statsApi } from '@/services/stats.service';
import type {
  ActivityFeedItem,
  CampusActivityItem,
  DashboardOverview,
  TimeseriesPoint,
} from '@/types/stats.types';

export const DASHBOARD_QUERY_KEY = 'dashboard';

export interface UseDashboardPageResult {
  greetingName: string;
  today: string;
  overview: DashboardOverview | undefined;
  timeseries: TimeseriesPoint[];
  campusActivity: CampusActivityItem[];
  activity: ActivityFeedItem[];
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  onOpenOnboardingQueue: () => void;
  onOpenReports: () => void;
  onOpenPayments: () => void;
}

/** "Good morning" / "Good afternoon" / "Good evening", as the prototype's header does. */
const greetingFor = (hour: number): string => {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const messageOf = (error: unknown): string | null =>
  error instanceof Error ? error.message : null;

export const useDashboardPage = (): UseDashboardPageResult => {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const now = new Date();

  const overviewQuery = useQuery({
    queryKey: [DASHBOARD_QUERY_KEY, 'overview'],
    queryFn: () => statsApi.overview(),
  });

  const timeseriesQuery = useQuery({
    queryKey: [DASHBOARD_QUERY_KEY, 'timeseries', TIMESERIES_DAYS],
    queryFn: () => statsApi.timeseries(TIMESERIES_DAYS),
  });

  const campusQuery = useQuery({
    queryKey: [DASHBOARD_QUERY_KEY, 'campus-activity'],
    queryFn: () => statsApi.campusActivity(),
  });

  const activityQuery = useQuery({
    queryKey: [DASHBOARD_QUERY_KEY, 'activity-feed'],
    queryFn: () => statsApi.activityFeed(),
  });

  return {
    // First name only — "Good morning, Arjun" reads better than the full name the prototype shows.
    greetingName: `${greetingFor(now.getHours())}, ${user?.name?.split(' ')[0] ?? 'there'}`,
    today: now.toLocaleDateString('en-IN', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
    overview: overviewQuery.data,
    timeseries: timeseriesQuery.data ?? [],
    campusActivity: campusQuery.data ?? [],
    activity: activityQuery.data ?? [],
    // Only the overview blocks the page — the panels below it show their own empty states.
    isLoading: overviewQuery.isLoading,
    isError: overviewQuery.isError,
    errorMessage: messageOf(overviewQuery.error),
    onOpenOnboardingQueue: () => navigate(`${ROUTES.users}?tab=incomplete`),
    onOpenReports: () => navigate(ROUTES.moderation),
    onOpenPayments: () => navigate(ROUTES.payments),
  };
};
